import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateOrganizationMutation } from "../graphql/generated/graphql";
import { useAdminTheme } from "../hooks/useAdminTheme";
import { ModalWrapper } from "./ModalWrapper";

/**
 * The canvas colours of the Tally forms themselves. Tally paints them inside its own document, so
 * they are raw values by necessity - the dark one happens to equal the package's dark base surface,
 * which is what lets the dark form sit in the dark modal without a visible frame.
 */
const TALLY_CANVAS = { light: "#fff", dark: "#201f1d" };

type TallyFormT = {
  dark: boolean;
  url: string;
};

type CampaignStopQuestionnairePropsT = {
  env: string;
  shoptetId: string;
  source: string;
  stopCampaignQuestionnairePending: boolean;
  userEmail: string;
};

export const CampaignStopQuestionnaire = ({
  env,
  shoptetId,
  source,
  stopCampaignQuestionnairePending,
  userEmail,
}: CampaignStopQuestionnairePropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Questionnaire" });
  const [updateOrganizationMutation] = useUpdateOrganizationMutation();
  const { dark: isDarkTheme, known: isThemeKnown } = useAdminTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [tallyForm, setTallyForm] = useState<TallyFormT>({ dark: false, url: "" });
  const formCaptured = useRef(false);

  // The form is captured once per questionnaire opening, theme included. A Tally
  // form cannot be restyled or re-pointed once loaded: changing the iframe src when
  // the submit handler clears the source property would reload it right after the
  // thank-you screen, and following a theme flip would reload it in the other
  // variant and discard every answer given so far - a cross-origin document cannot
  // carry its state over. So a flip keeps the form that is open, and which variant
  // that is travels with the URL so the card around it keeps matching.
  //
  // The capture waits for the theme to be settled. Nothing orders the admin's theme
  // message before the pending flag from the API, and picking early would risk the
  // light form in a dark admin with no way back. `adminTheme.ts` settles it without
  // a message too, so the questionnaire cannot be held up by a silent admin.
  useEffect(() => {
    if (!stopCampaignQuestionnairePending) {
      formCaptured.current = false;
      return;
    }
    if (formCaptured.current || !isThemeKnown) {
      return;
    }
    formCaptured.current = true;

    const url = new URL(t(isDarkTheme ? "tally url dark" : "tally url"));
    url.searchParams.set("email", userEmail);
    url.searchParams.set("id_shop", shoptetId);
    url.searchParams.set("zdroj", source);
    url.searchParams.set("env", env);
    setTallyForm({ dark: isDarkTheme, url: url.toString() });
    setModalOpen(true);
  }, [env, isDarkTheme, isThemeKnown, shoptetId, source, stopCampaignQuestionnairePending, t, userEmail]);

  const resetQuestionnairePending = useCallback(() => {
    updateOrganizationMutation({
      variables: { properties: { stopCampaignQuestionnairePending: false, stopCampaignQuestionnaireSource: null } },
    });
  }, [updateOrganizationMutation]);

  const handleFormCompletion = useCallback(
    (event: MessageEvent) => {
      const { data, origin } = event;
      if (origin === "https://tally.so" && data.includes("FormSubmitted")) {
        resetQuestionnairePending();
      }
    },
    [resetQuestionnairePending]
  );

  useEffect(() => {
    window.addEventListener("message", handleFormCompletion);
    return () => {
      window.removeEventListener("message", handleFormCompletion);
    };
  }, [handleFormCompletion]);

  const handleClose = () => {
    resetQuestionnairePending();
    setModalOpen(false);
  };

  return (
    <ModalWrapper isOpen={modalOpen} onClose={handleClose}>
      {/* Tally serves the form from a cross-origin iframe, so no CSS of ours applies inside it and
          the form cannot follow the theme the app is in - Tally has no theme switch in the URL
          either. Each language therefore has a light and a dark form, and the one matching the
          theme is loaded. The card is painted in that form's own canvas colour, so the frame never
          shows as a rectangle of a different shade inside the modal - not when the theme flips
          while the form is open, and not if either side's colours drift later. */}
      <div
        className={`questionnaire-card${tallyForm.dark ? "" : " questionnaire-card--light"}`}
        style={{
          background: tallyForm.dark ? TALLY_CANVAS.dark : TALLY_CANVAS.light,
          borderRadius: "var(--app-radius-box)",
          overflow: "hidden",
          padding: "8px",
        }}
      >
        {/* The embedded document has to be told which colour scheme it is, or the browser paints the
            frame's canvas in the surrounding theme behind it until the form loads - dark behind a
            light form and the other way round. */}
        <iframe
          src={tallyForm.url}
          title="Campaign Stop Questionnaire"
          style={{
            width: "100%",
            height: "400px",
            border: "none",
            display: "block",
            colorScheme: tallyForm.dark ? "dark" : "light",
          }}
        ></iframe>
      </div>
    </ModalWrapper>
  );
};

export default CampaignStopQuestionnaire;
